import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/Ui/Button';
import { PasswordField } from '@/components/Ui/Form/Rhf/PasswordField';
import auth from '@/helpers/auth';

import { loginSchema, type LoginValues } from './LoginPage.schema';

const LoginPage = function () {
    const navigate = useNavigate();
    const [
        t,
    ] = useTranslation('pages');

    const form = useForm({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: '', password: '' },
    });
    const { handleSubmit, register } = form;

    const login = async ({ email, password }: LoginValues) => {
        await axios.post('/api/auth/login', { email, password }).then(({ data }) => {
            // SAM-007 — three possible response shapes from /auth/login:
            //   1. User object (data.uuid set)   → session issued, go home
            //   2. { intent: 'enroll', token }   → admin must enroll 2FA, go to /connect-2fa
            //   3. Raw JWT string                → admin must complete 2FA challenge
            // The backend guarantees (1) only happens when 2FA is already
            // satisfied (workspace user or enrolled admin), so the old
            // `is_2fa_enabled || !isAdministrator()` check is no longer needed.
            if (data.uuid) {
                auth.setUser(data);
                navigate(auth.getInitialPath());
            } else if (data && data.intent === 'enroll') {
                navigate({
                    pathname: '/connect-2fa',
                    search: `?token=${ data.token }`,
                });
            } else {
                navigate({
                    pathname: '/confirm-login',
                    search: `?token=${ data }`,
                });
            }
        }).catch(() => {
            toast.error(
                t('Your login credentials were incorrect or your user is currently not enabled',
                    { removeDelay: 500 }
                )
            );
        });
    };

    const inputClasses = 'relative block w-full appearance-none rounded-none border border-input px-3 py-2 text-foreground placeholder-muted-foreground ring-offset-primary focus-visible:outline-none focus:z-10 focus-visible:ring-1 focus:outline-none focus-visible:ring-offset-1 sm:text-sm';

    return (
        <div className='max-w-md'>
            <div className='text-center text-foreground'>
                <p className='font-bold text-xl'>{t('Login Dashboard')}</p>
                <p className='mt-4'>{t('Enter your email address and password. If you do not have an account yet, you can')} <NavLink className='text-primary' to='/register'>{t('register your company')}</NavLink>.</p>
            </div>

            <FormProvider { ...form }>
                <form className='mt-8 space-y-6' onSubmit={ handleSubmit(login) }>
                    <input defaultValue='true' name='remember' type='hidden' />
                    <div className='-space-y-px rounded-md shadow-sm'>
                        <div>
                            <label className='sr-only' htmlFor='email'>
                                {t('Email')}
                            </label>
                            <input
                                autoComplete='email'
                                className={ `${ inputClasses } rounded-t-md` }
                                id='email'
                                placeholder={ t('Email') }
                                required
                                type='text'
                                { ...register('email') }
                            />
                        </div>
                        <div>
                            <label className='sr-only' htmlFor='password'>
                                {t('Password')}
                            </label>
                            <PasswordField
                                className={ `${ inputClasses } rounded-b-md` }
                                id='password'
                                name='password'
                                placeholder={ t('Password') }
                            />
                        </div>
                    </div>

                    <div className='pt-4 flex flex-col gap-y-6'>
                        <Button type='submit'>
                            <ArrowRightStartOnRectangleIcon aria-hidden='true' className='h-5 w-5' />
                            {t('Sign in')}
                        </Button>

                        <div className='text-center pt-4 border-t border-border'>
                            <NavLink
                                className='group relative flex w-full text-sm justify-center rounded-md border border-transparent bg-transparent hover:text-primary'
                                to='/request-password-reset'
                            >
                                {t('I forgot my password')}
                            </NavLink>
                            <NavLink
                                className='group relative flex w-full text-sm justify-center rounded-md border border-transparent bg-transparent hover:text-primary mt-3'
                                to='/register'
                            >
                                {t('Register your company')}
                            </NavLink>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

export default LoginPage;
